import React from 'react';
import { Link } from 'react-router-dom';
import useScrollReveal from '../../hooks/useScrollReveal';

const frameworks = ['CSRD', 'ESRS', 'EU Taxonomy', 'CBAM', 'BRSR', 'ISO 14067', 'GHG Protocol'];

export default function Hero(): React.ReactElement {
  const ref = useScrollReveal();

  return (
    <section className="hero" id="hero" ref={ref as React.RefObject<HTMLElement>}>
      <div className="container">
        <div className="hero-grid">

          <div className="hero-content">
            <div className="hero-eyebrow">
              <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden="true">
                <circle cx="4" cy="4" r="4" fill="#67E8F9" />
              </svg>
              Actula by ESGTriage
            </div>

            <h1 className="hero-headline reveal">
              Sustainability intelligence for the enterprises that{' '}
              <span className="accent">cannot afford to get it wrong.</span>
            </h1>

            <p className="hero-subhead reveal reveal-delay-1">
              Actula &mdash; ESGTriage&rsquo;s sustainability platform &mdash; covers every obligation
              your enterprise faces: ESG data collection and reporting, CBAM compliance, Product
              Carbon Footprint, Corporate Carbon Footprint, and sustainable procurement. One
              platform. Five modules. Audit-ready from day one.
            </p>

            <div className="hero-ctas reveal reveal-delay-2">
              <Link to="/platform" className="btn btn-primary">Explore Actula</Link>
              <Link to="/contact" className="btn btn-ghost-light">Book a demo</Link>
            </div>

            <div className="hero-proof-strip reveal reveal-delay-3">
              <span className="hero-proof-label">Covers:</span>
              {frameworks.map((fw, i) => (
                <React.Fragment key={fw}>
                  <span className="hero-framework">{fw}</span>
                  {i < frameworks.length - 1 && (
                    <span className="hero-fw-divider">&middot;</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="hero-visual" aria-hidden="true">
            <svg viewBox="0 0 520 420" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Background glow */}
              <ellipse cx="260" cy="210" rx="200" ry="160" fill="rgba(8,145,178,0.06)" />

              {/* Central hub */}
              <rect x="185" y="155" width="150" height="110" rx="12" fill="rgba(15,45,74,0.95)" stroke="#0891B2" strokeWidth="1.5" />
              <text x="260" y="192" fill="#67E8F9" fontFamily="DM Mono,monospace" fontSize="11" textAnchor="middle" fontWeight="500">ACTULA</text>
              <text x="260" y="210" fill="#64748B" fontFamily="Inter,sans-serif" fontSize="8.5" textAnchor="middle">ONE DATA MODEL</text>
              <rect x="205" y="222" width="110" height="26" rx="5" fill="#0891B2" fillOpacity="0.18" />
              <text x="260" y="239" fill="#67E8F9" fontFamily="DM Mono,monospace" fontSize="8" textAnchor="middle">&#9679; PROCESSING</text>

              {/* Input nodes */}
              <rect x="25" y="60" width="120" height="60" rx="8" fill="rgba(15,45,74,0.7)" stroke="#1E3A5F" strokeWidth="1" />
              <text x="85" y="83" fill="#94A3B8" fontFamily="Inter,sans-serif" fontSize="8" textAnchor="middle">SUPPLIER DATA</text>
              <text x="85" y="100" fill="#67E8F9" fontFamily="DM Mono,monospace" fontSize="11" textAnchor="middle">PCF</text>
              <text x="85" y="113" fill="#64748B" fontFamily="Inter,sans-serif" fontSize="7" textAnchor="middle">per component</text>

              <rect x="25" y="170" width="120" height="60" rx="8" fill="rgba(15,45,74,0.7)" stroke="#1E3A5F" strokeWidth="1" />
              <text x="85" y="193" fill="#94A3B8" fontFamily="Inter,sans-serif" fontSize="8" textAnchor="middle">IMPORT DATA</text>
              <text x="85" y="210" fill="#D97706" fontFamily="DM Mono,monospace" fontSize="11" textAnchor="middle">CBAM</text>
              <text x="85" y="223" fill="#64748B" fontFamily="Inter,sans-serif" fontSize="7" textAnchor="middle">liability tracked</text>

              <rect x="25" y="280" width="120" height="60" rx="8" fill="rgba(15,45,74,0.7)" stroke="#1E3A5F" strokeWidth="1" />
              <text x="85" y="303" fill="#94A3B8" fontFamily="Inter,sans-serif" fontSize="8" textAnchor="middle">SCOPE 1/2/3</text>
              <text x="85" y="320" fill="#059669" fontFamily="DM Mono,monospace" fontSize="11" textAnchor="middle">CCF</text>
              <text x="85" y="333" fill="#64748B" fontFamily="Inter,sans-serif" fontSize="7" textAnchor="middle">auto-populated</text>

              {/* Output nodes */}
              <rect x="375" y="60" width="120" height="60" rx="8" fill="rgba(15,45,74,0.7)" stroke="#1E3A5F" strokeWidth="1" />
              <text x="435" y="83" fill="#94A3B8" fontFamily="Inter,sans-serif" fontSize="8" textAnchor="middle">CSRD / ESRS</text>
              <rect x="389" y="91" width="70" height="7" rx="2" fill="#0891B2" fillOpacity="0.7" />
              <text x="435" y="112" fill="#67E8F9" fontFamily="DM Mono,monospace" fontSize="8" textAnchor="middle">READY &#10003;</text>

              <rect x="375" y="170" width="120" height="60" rx="8" fill="rgba(15,45,74,0.7)" stroke="#1E3A5F" strokeWidth="1" />
              <text x="435" y="193" fill="#94A3B8" fontFamily="Inter,sans-serif" fontSize="8" textAnchor="middle">EU TAXONOMY</text>
              <rect x="389" y="201" width="47" height="7" rx="2" fill="#059669" fillOpacity="0.7" />
              <rect x="441" y="201" width="26" height="7" rx="2" fill="#1E3A5F" />
              <text x="435" y="222" fill="#059669" fontFamily="DM Mono,monospace" fontSize="8" textAnchor="middle">72% ALIGNED</text>

              <rect x="375" y="280" width="120" height="60" rx="8" fill="rgba(15,45,74,0.7)" stroke="#1E3A5F" strokeWidth="1" />
              <text x="435" y="303" fill="#94A3B8" fontFamily="Inter,sans-serif" fontSize="8" textAnchor="middle">CBAM OUTPUT</text>
              <text x="435" y="320" fill="#D97706" fontFamily="DM Mono,monospace" fontSize="11" textAnchor="middle">&#8364;82,400</text>
              <text x="435" y="333" fill="#64748B" fontFamily="Inter,sans-serif" fontSize="7" textAnchor="middle">liability calculated</text>

              {/* Flow paths */}
              <path className="flow-path" d="M145 90 Q165 90 185 190" stroke="#0891B2" strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
              <path className="flow-path" d="M145 200 L185 210" stroke="#D97706" strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
              <path className="flow-path" d="M145 310 Q165 310 185 230" stroke="#059669" strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
              <path className="flow-path" d="M335 195 Q355 90 375 90" stroke="#0891B2" strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
              <path className="flow-path" d="M335 210 L375 200" stroke="#059669" strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
              <path className="flow-path" d="M335 225 Q355 310 375 310" stroke="#D97706" strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />

              {/* Animated pulse dots */}
              <circle cx="165" cy="145" r="4" fill="#0891B2">
                <animate attributeName="opacity" values="1;0.2;1" dur="2.1s" repeatCount="indefinite" />
              </circle>
              <circle cx="355" cy="148" r="4" fill="#D97706">
                <animate attributeName="opacity" values="1;0.2;1" dur="2.6s" repeatCount="indefinite" />
              </circle>
              <circle cx="165" cy="265" r="4" fill="#059669">
                <animate attributeName="opacity" values="1;0.2;1" dur="1.9s" repeatCount="indefinite" />
              </circle>
            </svg>
          </div>

        </div>
      </div>
    </section>
  );
}
